'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface Props {
  onSuccess: (email: string) => void;
}

export default function LoginForm({ onSuccess }: Props) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<LoginFormData>({ defaultValues: { rememberMe: false } });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    // BACKEND: POST /api/auth/login with { email, password }
    await new Promise((r) => setTimeout(r, 1400));

    toast.success('Welcome back to IvyDirectory!');
    setIsLoading(false);
    onSuccess(data.email);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 animate-slide-up">
      <div>
        <h2 className="font-display text-2xl font-semibold text-navy mb-1">Welcome back</h2>
        <p className="text-sm text-[#5a6e96]">Sign in to your IvyDirectory account</p>
      </div>

      {/* Social auth */}
      <div className="grid grid-cols-2 gap-3">
        {[
          {
            label: 'Google',
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            ),
          },
          {
            label: 'Apple',
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
              </svg>
            ),
          },
        ].map((provider) => (
          <button
            key={`social-${provider.label}`}
            type="button"
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-blue-100 rounded-xl text-sm font-medium text-navy hover:bg-blue-50 hover:border-navy/20 transition-all duration-150 active:scale-95"
          >
            {provider.icon}
            {provider.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <hr className="flex-1 border-blue-100" />
        <span className="text-xs text-[#5a6e96] font-medium">or continue with email</span>
        <hr className="flex-1 border-blue-100" />
      </div>

      {/* Email */}
      <div>
        <label className="block text-sm font-medium text-navy mb-1.5" htmlFor="login-email">
          Email address
        </label>
        <input
          id="login-email"
          type="email"
          autoComplete="email"
          placeholder="you@university.edu"
          className={`w-full px-4 py-3 rounded-xl border bg-white text-navy placeholder-[#8fa3c8] text-sm transition-all duration-150 outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy ${
            errors.email ? 'border-red-400 bg-red-50/50' : 'border-blue-100 hover:border-navy/30'
          }`}
          {...register('email', {
            required: 'Email address is required',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
          })}
        />
        {errors.email && <p className="mt-1.5 text-xs text-red-500">{errors.email.message}</p>}
      </div>

      {/* Password */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-sm font-medium text-navy" htmlFor="login-password">
            Password
          </label>
          <button type="button" className="text-xs text-blue-600 hover:text-blue-700 transition-colors">
            Forgot password?
          </button>
        </div>
        <div className="relative">
          <input
            id="login-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder="••••••••••••"
            className={`w-full px-4 py-3 pr-12 rounded-xl border bg-white text-navy placeholder-[#8fa3c8] text-sm transition-all duration-150 outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy ${
              errors.password ? 'border-red-400 bg-red-50/50' : 'border-blue-100 hover:border-navy/30'
            }`}
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 8, message: 'Password must be at least 8 characters' },
            })}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5a6e96] hover:text-navy transition-colors p-1"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {errors.password && <p className="mt-1.5 text-xs text-red-500">{errors.password.message}</p>}
      </div>

      {/* Remember me */}
      <div className="flex items-center gap-2.5">
        <input
          id="remember-me"
          type="checkbox"
          className="w-4 h-4 rounded border-blue-200 accent-navy cursor-pointer"
          {...register('rememberMe')}
        />
        <label htmlFor="remember-me" className="text-sm text-[#5a6e96] cursor-pointer">
          Remember me for 30 days
        </label>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={isLoading}
        className="w-full py-3.5 bg-navy text-white rounded-xl font-medium text-sm hover:bg-navy-light disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-150 active:scale-[0.99] flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
        style={{ minHeight: '48px' }}
      >
        {isLoading ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            Signing in...
          </>
        ) : (
          'Sign In to IvyDirectory'
        )}
      </button>
    </form>
  );
}